import type {
  CheckoutPaymentProviderOption,
  CheckoutPaymentProviderId,
  CheckoutAddress,
  CheckoutConfirmResult,
  CheckoutReview,
  CheckoutSession,
  CheckoutShippingMethod,
  CheckoutShippingMethodCode,
  CheckoutStep,
  CheckoutValidationError,
} from '../types'

export type CheckoutFlowErrorCode =
  | 'session_creation_failed'
  | 'session_load_failed'
  | 'session_expired'
  | 'empty_cart'
  | 'invalid_step'
  | 'backend_validation_error'
  | 'confirm_payment_failed'

export type CheckoutAddressForm = {
  firstName: string
  lastName: string
  company: string
  line1: string
  line2: string
  city: string
  postalCode: string
  region: string
  country: string
  phone: string
}

export type CheckoutFlowState = {
  bootstrapRequestId: number
  isBootstrapping: boolean
  isSubmitting: boolean
  isRedirecting: boolean
  sessionId: string | null
  session: CheckoutSession | null
  review: CheckoutReview | null
  shippingMethods: CheckoutShippingMethod[]
  step: CheckoutStep
  sameAsShipping: boolean
  shippingAddressForm: CheckoutAddressForm
  billingAddressForm: CheckoutAddressForm
  selectedShippingMethod: CheckoutShippingMethodCode
  paymentProviders: CheckoutPaymentProviderOption[]
  selectedPaymentProvider: CheckoutPaymentProviderId
  lastConfirmResult: CheckoutConfirmResult | null
  errorCode: CheckoutFlowErrorCode | null
  errorDetail: string
  validationErrors: CheckoutValidationError[]
}

export type CheckoutFlowAction =
  | { type: 'BOOTSTRAP_STARTED' }
  | {
      type: 'BOOTSTRAP_SUCCEEDED'
      session: CheckoutSession
      shippingAddressForm: CheckoutAddressForm
      billingAddressForm: CheckoutAddressForm
      step: CheckoutStep
      selectedShippingMethod: CheckoutShippingMethodCode
      selectedPaymentProvider: CheckoutPaymentProviderId
    }
  | { type: 'BOOTSTRAP_FAILED'; code: CheckoutFlowErrorCode; detail?: string }
  | { type: 'RETRY_BOOTSTRAP' }
  | { type: 'SET_STEP'; step: CheckoutStep }
  | { type: 'SET_ERROR'; code: CheckoutFlowErrorCode; detail?: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_VALIDATION_ERRORS'; validationErrors: CheckoutValidationError[] }
  | { type: 'CLEAR_VALIDATION_ERRORS' }
  | { type: 'SET_SAME_AS_SHIPPING'; value: boolean }
  | { type: 'SET_SHIPPING_FORM'; value: CheckoutAddressForm }
  | { type: 'SET_BILLING_FORM'; value: CheckoutAddressForm }
  | { type: 'SET_SHIPPING_FIELD'; field: keyof CheckoutAddressForm; value: string }
  | { type: 'SET_BILLING_FIELD'; field: keyof CheckoutAddressForm; value: string }
  | { type: 'SET_SELECTED_SHIPPING_METHOD'; value: CheckoutShippingMethodCode }
  | { type: 'SET_SELECTED_PAYMENT_PROVIDER'; value: CheckoutPaymentProviderId }
  | {
      type: 'SESSION_UPDATED'
      session: CheckoutSession
      shippingAddressForm: CheckoutAddressForm
      billingAddressForm: CheckoutAddressForm
      selectedShippingMethod: CheckoutShippingMethodCode
      selectedPaymentProvider?: CheckoutPaymentProviderId
      step: CheckoutStep
    }
  | {
      type: 'REVIEW_UPDATED'
      review: CheckoutReview
      shippingMethods: CheckoutShippingMethod[]
      paymentProviders: CheckoutFlowState['paymentProviders']
      defaultPaymentProvider: CheckoutPaymentProviderId
    }
  | { type: 'CONFIRM_COMPLETED'; result: CheckoutConfirmResult }
  | { type: 'SUBMIT_STARTED' }
  | { type: 'SUBMIT_COMPLETED' }
  | { type: 'REDIRECT_STARTED' }

export const EMPTY_ADDRESS_FORM: CheckoutAddressForm = {
  firstName: '',
  lastName: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  postalCode: '',
  region: '',
  country: 'IT',
  phone: '',
}

export const checkoutAddressToForm = (address: CheckoutAddress | null): CheckoutAddressForm => {
  if (!address) return EMPTY_ADDRESS_FORM
  return {
    firstName: address.firstName,
    lastName: address.lastName,
    company: address.company ?? '',
    line1: address.line1,
    line2: address.line2 ?? '',
    city: address.city,
    postalCode: address.postalCode,
    region: address.region ?? '',
    country: address.country,
    phone: address.phone ?? '',
  }
}

export const checkoutFormToAddress = (form: CheckoutAddressForm): CheckoutAddress => {
  const company = form.company.trim()
  const line2 = form.line2.trim()
  const region = form.region.trim()
  const phone = form.phone.trim()

  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    ...(company ? { company } : {}),
    line1: form.line1.trim(),
    ...(line2 ? { line2 } : {}),
    city: form.city.trim(),
    postalCode: form.postalCode.trim(),
    ...(region ? { region } : {}),
    country: form.country.trim().toUpperCase(),
    ...(phone ? { phone } : {}),
  }
}

const hasShippingAddress = (session: CheckoutSession) => Boolean(session.shippingAddress)

const hasBillingAddress = (session: CheckoutSession) => {
  if (session.sameAsShipping) return Boolean(session.shippingAddress)
  return Boolean(session.billingAddress)
}

const hasShippingMethod = (session: CheckoutSession) => Boolean(session.shippingMethod)

export const canNavigateToStep = (session: CheckoutSession, targetStep: CheckoutStep): boolean => {
  if (targetStep === 'cart_review') return true
  if (targetStep === 'shipping_address') return session.currentStep !== 'completed' && session.currentStep !== 'payment'
  if (targetStep === 'billing_address') return hasShippingAddress(session)
  if (targetStep === 'shipping_method') return hasShippingAddress(session) && hasBillingAddress(session)
  if (targetStep === 'review') return hasShippingAddress(session) && hasBillingAddress(session) && hasShippingMethod(session)
  if (targetStep === 'redirecting_to_payment') return session.currentStep === 'payment' || session.status === 'payment_pending'
  if (targetStep === 'completed') return session.currentStep === 'completed' || session.status === 'completed'
  return false
}

export const resolveInitialStepFromSession = (session: CheckoutSession): CheckoutStep => {
  return resolveNextStepFromSession(session)
}

export const resolveNextStepFromSession = (session: CheckoutSession): CheckoutStep => {
  switch (session.currentStep) {
    case 'shipping_address':
      return 'shipping_address'
    case 'billing_address':
      return 'billing_address'
    case 'shipping_method':
      return 'shipping_method'
    case 'review':
      return 'review'
    case 'payment':
      return 'redirecting_to_payment'
    case 'completed':
      return 'completed'
    default:
      return 'shipping_address'
  }
}

export const resolveBackStep = ({
  step,
  sameAsShipping,
}: {
  step: CheckoutStep
  sameAsShipping: boolean
}): CheckoutStep | null => {
  if (step === 'shipping_address') return 'cart_review'
  if (step === 'billing_address') return 'shipping_address'
  if (step === 'shipping_method') return sameAsShipping ? 'shipping_address' : 'billing_address'
  if (step === 'review') return 'shipping_method'
  return null
}
